/**
 * Entrar al equipo de un sitio ajeno.
 *
 * Hermana de [`TransferAccept`](./TransferAccept.tsx) y con la misma forma, pero
 * la decisión que se toma aquí es mucho más ligera: aceptar no te hace
 * responsable de nada, sólo te da acceso, y salirse es un clic. De ahí que no
 * lleve el aparato de avisos de aquella —ni inventario, ni encaje de plan—.
 *
 * Lo que sí conserva es lo importante: se enseña EXACTAMENTE a qué entras antes
 * de decidir. Un «te han invitado a un proyecto» sin decir a qué da acceso el
 * rol es lo que hace que la gente no acepte, o que acepte sin mirar; las dos
 * cosas son malas.
 *
 * A pantalla completa y sin el panel alrededor: quien llega aquí muchas veces
 * acaba de crear su cuenta y todavía no tiene nada suyo que enmarcar.
 */
import { useEffect, useState } from 'react'
import { longDate } from '../lib/dates'
import { IconAlertTriangle, IconCheck, IconLogout, IconX } from '@tabler/icons-react'
import { PROJECT_ROLES, isProjectRole, sectionsForRole } from '@webbuilder/shared-types'
import { acceptTeamInvite, fetchTeamInvite, rejectTeamInvite, type TeamInviteOffer } from '../lib/api'
import { useI18n, translateServerError } from '../lib/i18n'

export function TeamInviteAccept({ token, inviteToken, onDone, onChangeAccount }: {
  token: string
  inviteToken: string
  onDone: () => void
  /** Conserva el enlace, cierra esta sesión y lleva al acceso con otra cuenta. */
  onChangeAccount?: (() => Promise<void>) | undefined
}) {
  const { t } = useI18n()
  const [offer, setOffer] = useState<TeamInviteOffer | null>(null)
  /**
   * El error, en dos piezas: lo que dijo el servidor y la clave de reserva.
   *
   * Se guarda la CLAVE y no el texto ya traducido porque el efecto de abajo es
   * quien lo escribe: traducir ahí obligaría a poner `t` en sus dependencias, y
   * entonces cambiar de idioma volvería a pedirle la invitación al servidor.
   * `serverMessage` con cadena vacía deja distinguir «el servidor dijo algo» de
   * «no dijo nada y hay que poner lo nuestro».
   */
  const [error, setError] = useState<{ message: string; key: 'teamInvite.unavailable' | 'teamInvite.actionFailed' | 'teamInvite.signOutFailed' } | null>(null)
  const errorText = error ? (error.message || t(error.key)) : null
  const [busy, setBusy] = useState<'accept' | 'reject' | null>(null)
  const [changingAccount, setChangingAccount] = useState(false)
  const [wrongAccount, setWrongAccount] = useState(false)

  useEffect(() => {
    let alive = true
    void fetchTeamInvite(inviteToken, token)
      .then((data) => { if (alive) setOffer(data) })
      .catch((err) => {
        if (alive) {
          setWrongAccount(err instanceof Error && err.message.startsWith('403:'))
          setError({ message: translateServerError(err, ''), key: 'teamInvite.unavailable' })
        }
      })
    return () => { alive = false }
  }, [inviteToken, token])

  async function decide(action: 'accept' | 'reject') {
    setBusy(action)
    setError(null)
    try {
      if (action === 'accept') await acceptTeamInvite(inviteToken, token)
      else await rejectTeamInvite(inviteToken, token)
      onDone()
    } catch (err) {
      setError({ message: translateServerError(err, ''), key: 'teamInvite.actionFailed' })
      setBusy(null)
    }
  }

  if (error && !offer) {
    return (
      <main className="dash-transfer-screen">
        <div className="dash-placeholder dash-loaded-content-in">
          <IconAlertTriangle size={20} aria-hidden="true" />
          <strong>{t(wrongAccount ? 'teamInvite.wrongAccountTitle' : 'teamInvite.unavailableTitle')}</strong>
          <p>{errorText}</p>
          <div className="dash-transfer-actions">
            <button type="button" className="dash-secondary-btn" onClick={onDone}>{t('teamInvite.goToDashboard')}</button>
            {wrongAccount && onChangeAccount ? (
              <button
                type="button"
                className="dash-primary-btn"
                disabled={changingAccount}
                onClick={() => {
                  setChangingAccount(true)
                  void onChangeAccount().catch(() => {
                    setChangingAccount(false)
                    setError({ message: '', key: 'teamInvite.signOutFailed' })
                  })
                }}
              >
                <IconLogout size={15} aria-hidden="true" />
                {t(changingAccount ? 'teamInvite.switching' : 'teamInvite.switchAccount')}
              </button>
            ) : null}
          </div>
        </div>
      </main>
    )
  }

  if (!offer) {
    return (
      <main className="dash-transfer-screen">
        <div className="dash-placeholder">{t('teamInvite.loading')}</div>
      </main>
    )
  }

  const definition = isProjectRole(offer.role) ? PROJECT_ROLES[offer.role] : null

  return (
    <main className="dash-transfer-screen dash-loaded-content-in">
      <header className="dash-transfer-head">
        <p>{t('teamInvite.eyebrow')}</p>
        <h1>{offer.projectName}</h1>
        <p>
          {offer.fromName ? <strong>{offer.fromName}</strong> : t('teamInvite.someone')}
          {' '}{t('teamInvite.invitedBy')}{' '}<strong>{t(offer.roleLabelKey)}</strong>.
        </p>
      </header>

      <section className="dash-transfer-card" aria-label={t('teamInvite.whatYouGet')}>
        <p className="dash-team-role-preview-summary">{t(offer.roleSummaryKey || definition?.summaryKey || '')}</p>

        {/* Las secciones que abre el rol, enumeradas. Es la única información
            que hace falta para decidir, así que va antes que los botones. */}
        <ul className="dash-team-role-preview-list">
          {(isProjectRole(offer.role) ? sectionsForRole(offer.role) : []).map((section) => (
            <li key={section}><IconCheck size={12} aria-hidden="true" />{t(section)}</li>
          ))}
        </ul>

        <p className="dash-transfer-note">
          {t('teamInvite.scopePrefix')}<strong>{t('teamInvite.scopeStrong')}</strong>
          {t('teamInvite.scopeSuffix')}
        </p>
      </section>

      {error ? (
        <p className="dash-team-invite-error" role="alert">
          <IconAlertTriangle size={14} aria-hidden="true" /> {errorText}
        </p>
      ) : null}

      {/* Rechazar como botón secundario y no como enlace escondido: decir que no
          es una respuesta legítima, y esconderla empuja a aceptar por inercia. */}
      <div className="dash-transfer-actions">
        <button type="button" className="dash-secondary-btn" disabled={busy !== null} onClick={() => void decide('reject')}>
          <IconX size={15} aria-hidden="true" /> {t('teamInvite.decline')}
        </button>
        <button type="button" className="dash-primary-btn" disabled={busy !== null} onClick={() => void decide('accept')}>
          <IconCheck size={15} aria-hidden="true" />
          {t(busy === 'accept' ? 'teamInvite.joining' : 'teamInvite.accept')}
        </button>
      </div>

      <p className="dash-transfer-expiry">
        {t('teamInvite.expiry', { email: offer.email, date: longDate(offer.expiresAt) })}
      </p>
    </main>
  )
}
